import { debounce } from 'lodash'

import { PlainProduct } from '../hooks/useProducts'

export enum ContentGroups {
  blogPostPage = 'blog_page',
  campaignPage = 'campaign_page',
  methodPage = 'method_page',
  productPage = 'product_page',
  productsPage = 'testing_services',
  solutionPage = 'solution_page',
  ordersPage = 'projects_page',
  orderSubmissionPage = 'order_submission_page',
  offersPage = 'offers_page',
}

export const configureGa4 = ({
  contentGroup,
  user_id,
}: {
  contentGroup?: ContentGroups
  user_id?: string
}) => {
  // extra check to prevent logic running on SSR
  if (typeof window === 'undefined') {
    return
  }

  if (!window.dataLayer) {
    // eslint-disable-next-line no-console
    console.debug(
      'GTM is not loaded, skipping analytics configuration',
      'content_group',
      contentGroup,
      'user_id',
      user_id,
    )
    return
  }

  // nb: Googles docs have outdated info using 'set' instead of 'config' source: https://stackoverflow.com/questions/76174767/google-analytics-ga4-content-group
  // https://developers.google.com/analytics/devguides/collection/ga4/user-id
  window.dataLayer.push('config', {
    content_group: contentGroup,
    user_id,
  })

  // Send a custom event to Google Tag Manager to trigger GA4 initialization. We want to wait until the user params are set before configuring GA 4 to include user params in the first page view.
  window.dataLayer.push({
    event: 'user_params_configured',
  })
}

const toGoogleItem: (
  product: Pick<PlainProduct, 'id' | 'name'>,
  index?: number,
) => Gtag.Item = (product, index) => ({
  index: index === undefined ? undefined : index + 1,
  item_id: product.id,
  item_name: product.name,
})

const gtagEvent = (
  eventName: string,
  eventParams?: Gtag.ControlParams | Gtag.EventParams | Gtag.CustomParams,
) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      ...eventParams,
      event: eventName,
    })
  } else {
    // eslint-disable-next-line no-console
    console.debug(
      'GTM is not loaded, skipping analytics event',
      eventName,
      eventParams,
    )
  }
}

export const queueSearchInputPush = debounce((searchInput: string) => {
  gtagEvent('view_search_results', {
    search_term: searchInput.trim(),
  })
}, 1500)

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item_list
const productsShown = (viewName: string, products: PlainProduct[]) =>
  gtagEvent('view_item_list', {
    _clear: true,
    item_list_name: viewName,
    items: products.map(toGoogleItem),
  })

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events#select_item
const productClicked = (product: Pick<PlainProduct, 'id' | 'name'>) =>
  gtagEvent('select_item', {
    items: [toGoogleItem(product)],
  })

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item
const productDetailsViewed = (product: Pick<PlainProduct, 'id' | 'name'>) =>
  gtagEvent('view_item', {
    items: [toGoogleItem(product)],
  })

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_to_cart
const productAddedToCart = (product: Pick<PlainProduct, 'id' | 'name'>) =>
  gtagEvent('add_to_cart', {
    items: [toGoogleItem(product)],
  })

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events#remove_from_cart
const productRemovedFromCart = (product: Pick<PlainProduct, 'id' | 'name'>) =>
  gtagEvent('remove_from_cart', {
    items: [toGoogleItem(product)],
  })

// custom events
const leadFormSubscribed = (lead_tag: string) =>
  gtagEvent('lead_form_submit', { lead_tag })

const emailCopied = (email: string, location?: string) =>
  gtagEvent('email_copied', { email, location })

const fileDownloaded = () => gtagEvent('file_downloaded')

const orderFieldChanged = (fieldNames: string[]) =>
  gtagEvent('project_field_changed', { _clear: true, fieldNames })

const filterChanged = (filterName: string) =>
  gtagEvent('filter_changed', { filterName })

const orderOnlineClicked = () => gtagEvent('order_online_clicked')

export const gaEvents = {
  emailCopied,
  fileDownloaded,
  filterChanged,
  leadFormSubscribed,
  orderFieldChanged,
  orderOnlineClicked,
  productAddedToCart,
  productClicked,
  productDetailsViewed,
  productRemovedFromCart,
  productsShown,
  queueSearchInputPush,
}
